<script setup lang="ts">
import { TransitionSlide, TransitionExpand } from '#components'

type TOptions = {
  relative?: boolean
  isExternal?: boolean
  isExternalNoOpener?: boolean
  isExternalNoReferrer?: boolean
  mobile?: boolean
}

type TProps = {
  title?: TNullable<string>
  url?: TNullable<string>
  subMenuItems?: TNullable<TSubMenuItem>
  options?: TOptions
}

const stateMenuOpen = ref(false)

const props = withDefaults(defineProps<TProps>(), {
  title: null,
  url: '/',
  subMenuItems: void 0,
  options: () => ({
    relative: true,
    isExternal: false,
    isExternalNoOpener: true,
    isExternalNoReferrer: true,
    mobile: false,
  }),
})

const isSubMenu = computed(
  () => props.subMenuItems && props.subMenuItems?.links?.length > 0
)

const onMenuItemClick = async () => {
  if (!isSubMenu.value && props.url && !props.options?.isExternal) {
    return await navigateTo(props.url)
  }

  if (!isSubMenu.value && props.url && props.options?.isExternal) {
    return await navigateTo(props.url, { external: true })
  }

  stateMenuOpen.value = !stateMenuOpen.value
}

const menuItemRef = useTemplateRef<HTMLDivElement>('menuItemRef')

const { isMobile } = useDevice()

onClickOutside(menuItemRef, () => {
  if (isMobile) return

  stateMenuOpen.value = false
})

const transitionComponentName = computed(() => {
  return !props.options?.mobile ? TransitionSlide : TransitionExpand
})
</script>

<template>
  <div
    ref="menuItemRef"
    class="w-full justify-start items-start gap-1 p-2 lg:p-0 inline-flex flex-col cursor-pointer"
    :class="{
      relative: props.options?.relative,
    }"
    @click.prevent="onMenuItemClick"
  >
    <button
      type="button"
      class="w-full inline-flex justify-between lg:will-change-auto lg:transition-colors lg:duration-300 lg:rounded-lg lg:px-4 lg:py-2 lg:hover:bg-trail-surface-tertiary-normal lg:justify-start items-center gap-1"
      :data-testid="
        isSubMenu ? 'header-menu-item-dropdown' : 'header-menu-item-nav-link'
      "
    >
      <div class="text-trail-text-title-normal text-trail-title4">
        {{ props.subMenuItems?.title || props.title }}
      </div>

      <NuxtIcon
        v-if="isSubMenu"
        :class="[
          'text-gray-700 transition-all duration-300 ease-in-out',
          { 'rotate-180': stateMenuOpen },
        ]"
        name="heroicons:chevron-down"
      />
    </button>

    <component
      :is="transitionComponentName"
      :duration="{ enter: 500, leave: 100 }"
      easing="linear(0, 0.004 0.1%, 0.09 1.8%, 0.159 3.3%, 0.227 4.9%, 0.29 6.5%, 0.351 8.2%, 0.41 10%, 0.464 11.8%, 0.515 13.7%, 0.564 15.7%, 0.608 17.7%, 0.649 19.8%, 0.686 21.9%, 0.722 24.2%, 0.754 26.5%, 0.785 29%, 0.812 31.6%, 0.837 34.2%, 0.858 36.9%, 0.878 39.7%, 0.896 42.7%, 0.912 45.8%, 0.926 49.1%, 0.938 52.6%, 0.95 56.4%, 0.967 64.6%, 0.98 74.2%, 0.989 85.7%, 1)"
      group
      appear
    >
      <div
        v-if="stateMenuOpen"
        :class="[
          'w-full bg-white justify-center items-center gap-4 inline-flex',
          {
            'absolute top-full left-0 flex-col min-w-[13.5rem] p-2 shadow-full-lg z-20 rounded-2xl items-center justify-center':
              !props.options?.mobile,
            'relative py-4 md:py-8': props.options?.mobile,
          },
        ]"
      >
        <div class="w-full flex-col justify-center items-center flex">
          <div
            class="flex-col px-4 lg:px-0 justify-center items-start gap-2 flex w-full"
            data-testid="header-menu-item-links-container"
          >
            <NuxtLink
              v-for="(link, index) in props.subMenuItems?.links"
              :key="`${props.subMenuItems?.title}-${link.text}-${index}`"
              class="min-h-[2.75rem] text-trail-body4 text-trail-text-body-normal inline-flex items-center justify-start w-full lg:hover:bg-trail-surface-tertiary-normal rounded-lg px-2"
              :to="link.url"
              data-testid="header-menu-item-link"
            >
              {{ link.text }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>
